import type { ParamSpec } from "@cfg/design-sdk";
import type { ParamWidget, ParamWidgetProps } from "@cfg/brand-sdk";
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useOptionalConfiguratorContext } from "./context.tsx";
import { createDebouncer, fillPercent, parseNumberInput } from "./timing.ts";

/* ----------------------------- widget registry ----------------------------- */

const RegistryCtx = createContext<Record<string, ParamWidget>>({});

/** UI strings used inside the default widgets; `{value}` is the bound with its unit ("50 mm"). */
export interface WidgetText {
  clampedMin: string;
  clampedMax: string;
}
const defaultText: WidgetText = { clampedMin: "Min {value}", clampedMax: "Max {value}" };
const TextCtx = createContext<WidgetText>(defaultText);

export function WidgetRegistryProvider({ widgets, text, children }: { widgets?: Record<string, ParamWidget>; text?: Partial<WidgetText>; children: ReactNode }) {
  const merged = useMemo(() => ({ ...defaultText, ...text }), [text]);
  return (
    <RegistryCtx.Provider value={widgets ?? {}}>
      <TextCtx.Provider value={merged}>{children}</TextCtx.Provider>
    </RegistryCtx.Provider>
  );
}

/** Accessibility wiring ParamField hands to the default widgets (print-check issue on this param). */
interface FieldA11y {
  invalid: boolean;
  describedBy?: string;
}
const FieldA11yCtx = createContext<FieldA11y>({ invalid: false });

const joinIds = (...ids: (string | null | undefined | false)[]) => ids.filter(Boolean).join(" ") || undefined;

/* ------------------------------ default widgets ---------------------------- */

function fmt(v: number, step?: number): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "";
  if (!step || step >= 1) return Number.isInteger(v) ? String(v) : v.toFixed(2);
  const decimals = Math.min(4, Math.max(0, Math.ceil(-Math.log10(step))));
  return v.toFixed(decimals);
}

/** Typed values commit after this pause; arrow keys, spinners and the slider commit at once. */
export const NUMBER_COMMIT_DELAY_MS = 250;

type NumberAction = { kind: "commit"; value: number } | { kind: "hint"; which: "min" | "max" };

/**
 * Slider + number field. The slider is pointer-only in the tab order (`tabIndex=-1`; the number
 * field takes arrow keys), exposes `--cfg-fill` (0–100%) for a filled track, and marks the param as
 * being dragged. The number field shows the unit as a `.cfg-input-suffix`, commits typed in-range
 * values after 250 ms, and when a value is clamped shows a `.cfg-clamp-hint` ("Max 50 mm").
 */
export function NumberWidget({ spec, value, onChange, disabled, invalid }: ParamWidgetProps<number>) {
  const id = useId();
  const text = useContext(TextCtx);
  const a11y = useContext(FieldA11yCtx);
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(fmt(value, spec.step));
  const [hint, setHint] = useState<"min" | "max" | null>(null);
  const stepKey = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const hintTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const debouncer = useMemo(
    () =>
      createDebouncer<NumberAction>((a) => {
        if (a.kind === "commit") onChangeRef.current(a.value);
        else setHint(a.which);
      }, NUMBER_COMMIT_DELAY_MS),
    [],
  );
  useEffect(
    () => () => {
      debouncer.cancel();
      clearTimeout(hintTimer.current);
    },
    [debouncer],
  );
  // Follow outside changes (slider, presets, reset) unless the field is focused and already says this.
  useEffect(() => {
    const el = inputRef.current;
    if (el && typeof document !== "undefined" && document.activeElement === el) {
      const p = parseNumberInput(el.value, spec);
      if (debouncer.pending || (p.kind === "ok" && !p.clamped && p.value === value)) return;
    }
    setDraft(fmt(value, spec.step));
  }, [value, spec.step]);

  const hasRange = spec.min !== undefined && spec.max !== undefined;
  const step = spec.step ?? (spec.type === "int" ? 1 : 0.1);
  const showHint = (which: "min" | "max") => {
    setHint(which);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHint(null), 3000);
  };
  /** Blur / Enter: commit now, clamping out-of-range values (with a hint) and reverting junk. */
  const commitNow = (raw: string) => {
    debouncer.cancel();
    const p = parseNumberInput(raw, spec);
    if (p.kind !== "ok") return setDraft(fmt(value, spec.step));
    if (p.clamped) showHint(p.clamped);
    if (p.value !== value) onChange(p.value);
    setDraft(fmt(p.value, spec.step));
  };
  const onType = (raw: string, immediate: boolean) => {
    setDraft(raw);
    const p = parseNumberInput(raw, spec);
    if (p.kind !== "ok") {
      debouncer.cancel();
      setHint(null);
      return;
    }
    if (p.clamped) {
      // Out of range while typing: say so after the pause, commit (clamped) on blur/Enter.
      if (immediate) return commitNow(raw);
      debouncer.schedule({ kind: "hint", which: p.clamped });
      return;
    }
    setHint(null);
    if (immediate) {
      debouncer.cancel();
      if (p.value !== value) onChange(p.value);
    } else debouncer.schedule({ kind: "commit", value: p.value });
  };

  const unit = spec.unit ? ` ${spec.unit}` : "";
  const hintText = hint ? (hint === "min" ? text.clampedMin : text.clampedMax).replace("{value}", `${fmt(hint === "min" ? spec.min! : spec.max!, spec.step)}${unit}`) : "";
  const helpId = spec.description ? `${id}-help` : undefined;
  const errorId = invalid ? `${id}-error` : undefined;
  const hintId = `${id}-hint`;
  const fill = hasRange ? fillPercent(value, spec.min!, spec.max!) : 0;

  return (
    <div className={`cfg-field cfg-field-number${invalid ? " is-invalid" : ""}`} data-param={spec.name}>
      <label className="cfg-label" htmlFor={id}>
        <span>{spec.label}</span>
        {spec.unit && <span className="cfg-unit cfg-visually-hidden"> ({spec.unit})</span>}
      </label>
      <div className="cfg-number-row">
        {hasRange && (
          <input
            className="cfg-slider"
            type="range"
            min={spec.min}
            max={spec.max}
            step={step}
            value={value}
            disabled={disabled}
            tabIndex={-1}
            aria-label={spec.label}
            aria-invalid={a11y.invalid || undefined}
            style={{ "--cfg-fill": `${fill}%` } as CSSProperties}
            onChange={(e) => {
              debouncer.cancel();
              setHint(null);
              onChange(spec.type === "int" ? parseInt(e.target.value, 10) : parseFloat(e.target.value));
            }}
          />
        )}
        <div className={`cfg-input-wrap${spec.unit ? " has-suffix" : ""}`} style={spec.unit ? ({ "--cfg-suffix-len": spec.unit.length } as CSSProperties) : undefined}>
          <input
            ref={inputRef}
            id={id}
            className={`cfg-input cfg-input-number${a11y.invalid ? " is-invalid" : ""}`}
            type="number"
            inputMode="decimal"
            min={spec.min}
            max={spec.max}
            step={step}
            value={draft}
            disabled={disabled}
            aria-invalid={a11y.invalid || !!invalid || undefined}
            aria-describedby={joinIds(a11y.describedBy, errorId, hint && hintId, helpId)}
            onChange={(e) => {
              const ne = e.nativeEvent as Event & { inputType?: string };
              // Spinner clicks and arrow keys arrive without an inputType: commit at once.
              const immediate = stepKey.current || !ne.inputType;
              stepKey.current = false;
              onType(e.target.value, immediate);
            }}
            onBlur={(e) => commitNow(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "PageUp" || e.key === "PageDown") stepKey.current = true;
              if (e.key === "Enter") commitNow((e.target as HTMLInputElement).value);
            }}
          />
          {spec.unit && (
            <span className="cfg-input-suffix" aria-hidden>
              {spec.unit}
            </span>
          )}
        </div>
      </div>
      <p className="cfg-clamp-hint" id={hintId} aria-live="polite">
        {hintText}
      </p>
      {spec.description && (
        <p className="cfg-help" id={helpId}>
          {spec.description}
        </p>
      )}
      {invalid && (
        <p className="cfg-error" id={errorId}>
          {invalid}
        </p>
      )}
    </div>
  );
}

export function BoolWidget({ spec, value, onChange, disabled }: ParamWidgetProps<boolean>) {
  const id = useId();
  const a11y = useContext(FieldA11yCtx);
  const helpId = spec.description ? `${id}-help` : undefined;
  return (
    <div className="cfg-field cfg-field-bool" data-param={spec.name}>
      <label className="cfg-switch" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={value}
          disabled={disabled}
          aria-invalid={a11y.invalid || undefined}
          aria-describedby={joinIds(a11y.describedBy, helpId)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="cfg-switch-track" aria-hidden />
        <span className="cfg-label">{spec.label}</span>
      </label>
      {spec.description && (
        <p className="cfg-help" id={helpId}>
          {spec.description}
        </p>
      )}
    </div>
  );
}

export function SelectWidget({ spec, value, onChange, disabled }: ParamWidgetProps<string>) {
  const id = useId();
  const a11y = useContext(FieldA11yCtx);
  const options = spec.options ?? [];
  const segmented = options.length <= 4;
  const helpId = spec.description ? `${id}-help` : undefined;
  return (
    <div className="cfg-field cfg-field-select" data-param={spec.name}>
      <label className="cfg-label" htmlFor={id} id={`${id}-label`}>
        {spec.label}
      </label>
      {segmented ? (
        <div className="cfg-segmented" role="radiogroup" aria-labelledby={`${id}-label`} aria-invalid={a11y.invalid || undefined} aria-describedby={joinIds(a11y.describedBy, helpId)}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={o.value === value}
              className={`cfg-segment${o.value === value ? " is-active" : ""}`}
              disabled={disabled}
              onClick={() => onChange(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <select
          id={id}
          className={`cfg-input${a11y.invalid ? " is-invalid" : ""}`}
          value={value}
          disabled={disabled}
          aria-invalid={a11y.invalid || undefined}
          aria-describedby={joinIds(a11y.describedBy, helpId)}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      {spec.description && (
        <p className="cfg-help" id={helpId}>
          {spec.description}
        </p>
      )}
    </div>
  );
}

export function StringWidget({ spec, value, onChange, disabled }: ParamWidgetProps<string>) {
  const id = useId();
  const a11y = useContext(FieldA11yCtx);
  return (
    <div className="cfg-field" data-param={spec.name}>
      <label className="cfg-label" htmlFor={id}>
        {spec.label}
      </label>
      <input
        id={id}
        className={`cfg-input${a11y.invalid ? " is-invalid" : ""}`}
        type="text"
        value={value ?? ""}
        disabled={disabled}
        aria-invalid={a11y.invalid || undefined}
        aria-describedby={a11y.describedBy}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/* ----------------------------- field dispatcher ---------------------------- */

/** The worst print-check issue reported for one param. */
export interface FieldIssue {
  level: "info" | "warning" | "error";
  message: string;
}

export interface ParamFieldProps {
  spec: ParamSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  invalid?: string;
  /** Print-check issue naming this param: errors mark the field invalid and show the message inline. */
  issue?: FieldIssue;
  onFocus?: () => void;
}

/**
 * Picks the widget: brand override by `spec.widget` or type, else the default for the type.
 * The wrapper `#cfg-param-<name>.cfg-field-wrap` carries `data-param`, `data-issue=<level>`,
 * `.is-invalid` (error) and `.is-dragging` (its slider is held); an error renders
 * `.cfg-field-issue#cfg-issue-<name>`, which the field's inputs reference via `aria-describedby`.
 */
export function ParamField({ spec, value, onChange, disabled, invalid, issue, onFocus }: ParamFieldProps) {
  const registry = useContext(RegistryCtx);
  const ctx = useOptionalConfiguratorContext();
  const dragging = useDragging(ctx, spec.name);
  const wrapRef = useRef<HTMLDivElement>(null);
  const Override = (spec.widget && registry[spec.widget]) || registry[`type:${spec.type}`];
  const props = { spec, value, onChange, disabled, invalid } as ParamWidgetProps;
  const isError = issue?.level === "error";
  const issueId = isError ? `cfg-issue-${spec.name}` : undefined;
  const a11y = useMemo<FieldA11y>(() => ({ invalid: isError, describedBy: issueId }), [isError, issueId]);

  // Brand widgets don't read FieldA11yCtx: wire aria-invalid / aria-describedby onto their controls.
  useEffect(() => {
    if (!Override || !wrapRef.current) return;
    const controls = wrapRef.current.querySelectorAll<HTMLElement>("input, select, textarea, [role=radiogroup]");
    controls.forEach((el) => {
      const ids = (el.getAttribute("aria-describedby") ?? "").split(/\s+/).filter((t) => t && !t.startsWith("cfg-issue-"));
      if (issueId) ids.unshift(issueId);
      if (ids.length) el.setAttribute("aria-describedby", ids.join(" "));
      else el.removeAttribute("aria-describedby");
      if (isError) el.setAttribute("aria-invalid", "true");
      else el.removeAttribute("aria-invalid");
    });
  });

  const body = Override ? (
    <Override {...props} />
  ) : spec.type === "number" || spec.type === "int" ? (
    <NumberWidget {...(props as ParamWidgetProps<number>)} />
  ) : spec.type === "bool" ? (
    <BoolWidget {...(props as ParamWidgetProps<boolean>)} />
  ) : spec.type === "select" ? (
    <SelectWidget {...(props as ParamWidgetProps<string>)} />
  ) : (
    <StringWidget {...(props as ParamWidgetProps<string>)} />
  );
  const startDrag = (e: React.PointerEvent) => {
    onFocus?.();
    if (ctx && (e.target as HTMLElement).matches?.("input[type=range]")) ctx.store.getState().setDragParam(spec.name);
  };
  return (
    <div
      ref={wrapRef}
      className={`cfg-field-wrap${isError ? " is-invalid" : ""}${dragging ? " is-dragging" : ""}`}
      id={`cfg-param-${spec.name}`}
      data-param={spec.name}
      data-issue={issue?.level}
      onFocusCapture={onFocus}
      onPointerDownCapture={startDrag}
    >
      <FieldA11yCtx.Provider value={a11y}>{body}</FieldA11yCtx.Provider>
      {isError && (
        <p className="cfg-field-issue is-error" id={issueId}>
          {issue.message}
        </p>
      )}
    </div>
  );
}

/** Whether this param's slider is held; clears the store's drag flag on pointer release anywhere. */
function useDragging(ctx: ReturnType<typeof useOptionalConfiguratorContext>, name: string): boolean {
  const store = ctx?.store;
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!store) return;
    return store.subscribe((s, prev) => {
      if (s.dragParam === prev.dragParam) return;
      setDragging(s.dragParam === name);
      if (s.dragParam === name) {
        const end = () => {
          window.removeEventListener("pointerup", end);
          window.removeEventListener("pointercancel", end);
          if (store.getState().dragParam === name) store.getState().setDragParam(null);
        };
        window.addEventListener("pointerup", end);
        window.addEventListener("pointercancel", end);
      }
    });
  }, [store, name]);
  return dragging;
}
